import React, { useState, useEffect } from 'react';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import { useDepartmentTypes } from '../../hooks/useDepartments';

export default function DepartmentModal({
    isOpen,
    onClose,
    onSave,
    editData,
    title = 'Add Department',
    organizations = [],
    projects = [],
    isLoading
}) {
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        type: '',
        org: '',
        project: null,
        manager_employee: ''
    });
    const { data: typesData, isLoading: typesLoading } = useDepartmentTypes();
    const departmentTypes = typesData?.results || typesData || [];

    // Check if we're creating under a project (project context)
    const isProjectContext = editData?.project !== undefined;

    const getOrgType = (orgId) => {
        const org = organizations.find(o => String(o.id) === String(orgId));
        return org?.type_id || org?.org_type_id || org?.type?.id || org?.org_type || org?.type || '';
    };

    useEffect(() => {
        if (editData) {
            const orgId = editData.org_id || editData.org?.id || editData.org || '';
            const explicitType = editData.type_id || editData.type?.id || editData.type || '';
            setFormData({
                name: editData.name || '',
                code: editData.code || '',
                description: editData.description || '',
                type: explicitType || getOrgType(orgId),
                org: orgId,
                project: editData.project_id || editData.project?.id || editData.project || null,
                manager_employee: editData.manager_employee_id || editData.manager_employee?.id || editData.manager_employee || '',
            });
        } else {
            setFormData({
                name: '',
                code: '',
                description: '',
                type: '',
                org: '',
                project: null,
                manager_employee: '',
            });
        }
    }, [editData, isOpen, organizations]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleChange = (field, value) => {
        if (field === 'org') {
            setFormData(prev => ({ ...prev, org: value, type: prev.type || getOrgType(value) }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    // Auto-generate code from name (e.g., "Finance Department" -> "FINANCE_DEPARTMENT")
    const handleNameChange = (value) => {
        setFormData(prev => ({
            ...prev,
            name: value,
            code: value.toUpperCase().replace(/[^A-Z0-9\s]/g, '').replace(/\s+/g, '_'),
        }));
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed p-15 w-full max-w-[500px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1001]">
                <div className="py-10 md:px-20 px-15 bg-bg-dark rounded-lg shadow-xl border border-border">
                    {/* Header */}
                    <div className="flex items-center justify-between py-10 mb-15 border-b border-border">
                        <h5 className="text-[16px] font-medium">{title}</h5>
                        <button onClick={onClose} className="p-5 hover:bg-bg-light rounded-full">
                            <IconX size={18} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="space-y-15">
                            {/* Organization or Project dropdown based on context */}
                            {isProjectContext ? (
                                <div className="form-group">
                                    <label className="text-xs font-medium mb-5 block">
                                        Project <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        value={formData.project || ''}
                                        onChange={(e) => handleChange('project', e.target.value)}
                                        required
                                        className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                    >
                                        <option value="">Select Project</option>
                                        {projects.map(proj => (
                                            <option key={proj.id} value={proj.id}>{proj.name}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label className="text-xs font-medium mb-5 block">
                                        Organization <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        value={formData.org}
                                        onChange={(e) => handleChange('org', e.target.value)}
                                        required
                                        className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                    >
                                        <option value="">Select Organization</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Name */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">
                                    Department Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleNameChange(e.target.value)}
                                    placeholder="e.g., Finance Department"
                                    required
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                />
                            </div>

                            {/* Code */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">
                                    Code <span className="text-text-secondary">(Auto-generated)</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    readOnly
                                    placeholder="Auto-generated from name"
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none text-text-secondary uppercase"
                                />
                            </div>

                            {/* Type */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">
                                    Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => handleChange('type', e.target.value)}
                                    required
                                    disabled={typesLoading}
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                >
                                    <option value="">{typesLoading ? 'Loading...' : 'Select Type'}</option>
                                    {departmentTypes.map(type => (
                                        <option key={type.id || type.value} value={type.id || type.value}>
                                            {type.name || type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label className="text-xs font-medium mb-5 block">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Brief description of the department"
                                    rows={2}
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary resize-none"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-10 justify-end mt-20 pt-15 border-t border-border">
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn btn-outline-secondary btn-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn btn-primary btn-sm flex items-center gap-5"
                            >
                                {isLoading && <IconLoader2 size={16} className="animate-spin" />}
                                {editData?.id ? 'Update' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {/* Backdrop */}
            <div onClick={onClose} className="fixed z-[1000] w-full h-full left-0 top-0 bg-black/50 backdrop-blur-[2px]" />
        </>
    );
}
